import { Modal, ModalHeader, ModalBody, Spinner } from 'reactstrap';
import { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Context as AuthContext } from '../../../../../contexts/AuthContext';
import OneOnOneService from '../../../../../services/PerformanceOneOnOne';
import OneOnOneCard from './OneOnOneCard';
import SummaryModalRecordRow from '../components/SummaryModalRecordRow';

interface OneOnOneData {
  id: number;
  name: string;
  date_of_incident: string;
  dateOfIncident?: string;
  position: string;
  department: string;
  supervisor: string;
  date_of_hire: string;
  dateOfHire?: string;
  policy_violation: string;
  policyViolation?: { name: string };
  description_of_one_on_one: string;
  descriptionOfOneOnOne?: string;
  core_values: string[];
  coreValues?: string[];
  meeting_goal: string;
  meetingGoal?: string;
  private_notes?: string;
  privateNotes?: string;
  created_at: string;
  createdAt?: string;
  updated_at: string;
  updatedAt?: string;
}

interface OneOnOneItem {
  date_of_incident?: string;
  dateOfIncident?: string;
  type?: string;
  policy_violated?: string;
  policyViolations?: { name?: string }[];
  policyViolation?: { name: string };
  policy_violation?: string;
  data?: OneOnOneData;
  id?: number;
  name?: string;
  position?: string;
  department?: string;
  supervisor?: string;
  date_of_hire?: string;
  dateOfHire?: string;
  description_of_one_on_one?: string;
  descriptionOfOneOnOne?: string;
  core_values?: string[];
  coreValues?: string[];
  meeting_goal?: string;
  meetingGoal?: string;
  private_notes?: string;
  privateNotes?: string;
  created_at?: string;
  createdAt?: string;
}

interface OneOnOneSummaryModalProps {
  isOpen: boolean;
  toggle: () => void;
  userId?: string | null;
  userName?: string;
  oneOnOneData?: OneOnOneItem[];
}

const getOneOnOneListDescription = (item: OneOnOneItem) => {
  if (item.policyViolations && Array.isArray(item.policyViolations)) {
    const joined = item.policyViolations.map((pv: any) => pv.name || pv).join(', ');
    if (joined) {
      return joined;
    }
  }
  const pv =
    item.policy_violated ?? item.policyViolation?.name ?? item.policy_violation ?? item.data?.policy_violation ?? '';
  if (pv) {
    return pv;
  }
  return item.meeting_goal ?? item.meetingGoal ?? item.description_of_one_on_one ?? item.descriptionOfOneOnOne ?? '';
};

const OneOnOneSummaryModal = ({ isOpen, toggle, userId, userName, oneOnOneData }: OneOnOneSummaryModalProps) => {
  const [oneOnOnes, setOneOnOnes] = useState<OneOnOneItem[]>([]);
  const [selected, setSelected] = useState<OneOnOneItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { token }: any = useContext(AuthContext);

  const loadOneOnOnes = useCallback(async () => {
    if (!userId || !token) {
      return;
    }

    try {
      setIsLoading(true);
      const service = OneOnOneService(token);
      const response = await service.getUserOneOnOnes(userId, 'one-on-one');

      const oneOnOneDataResponse = response?.data || [];
      setOneOnOnes(oneOnOneDataResponse);
    } catch (error: any) {
      console.error('Error loading one on ones:', error);
      toast.error(error?.response?.data?.message || 'Error loading one on ones');
      setOneOnOnes([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      if (oneOnOneData) {
        setOneOnOnes(oneOnOneData);
      } else if (userId) {
        loadOneOnOnes();
      }
    }
  }, [isOpen, userId, oneOnOneData, loadOneOnOnes]);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return moment.utc(dateString).format('MM/DD/YYYY');
  };

  const handleClose = () => {
    setSelected(null);
    setOneOnOnes([]);
    toggle();
  };

  const renderDetailCard = (oneOnOne: OneOnOneItem, index: number) => (
    <OneOnOneCard
      key={oneOnOne.id || index}
      index={index}
      name={oneOnOne.name ?? ''}
      dateOfIncident={formatDate(oneOnOne.date_of_incident ?? oneOnOne.dateOfIncident)}
      position={oneOnOne.position ?? ''}
      department={oneOnOne.department ?? ''}
      supervisor={oneOnOne.supervisor ?? ''}
      dateOfHire={formatDate(oneOnOne.date_of_hire ?? oneOnOne.dateOfHire)}
      policyViolation={
        oneOnOne.policyViolations && Array.isArray(oneOnOne.policyViolations)
          ? oneOnOne.policyViolations.map((pv: any) => pv.name || pv).join(', ')
          : oneOnOne.policy_violated ?? oneOnOne.policyViolation?.name ?? oneOnOne.policy_violation ?? ''
      }
      descriptionOfOneOnOne={oneOnOne.description_of_one_on_one ?? oneOnOne.descriptionOfOneOnOne ?? ''}
      coreValues={oneOnOne.core_values ?? oneOnOne.coreValues ?? []}
      meetingGoal={oneOnOne.meeting_goal ?? oneOnOne.meetingGoal ?? ''}
      privateNotes={oneOnOne.private_notes ?? oneOnOne.privateNotes}
      createdAt={formatDate(oneOnOne.created_at ?? oneOnOne.createdAt)}
    />
  );

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      {selected ? (
        <div className="modal-header align-items-start">
          <button
            type="button"
            className="btn btn-link p-0 mr-3 text-dark mt-1"
            onClick={() => setSelected(null)}
            aria-label="Back"
          >
            <i className="fas fa-arrow-left fa-lg" />
          </button>
          <div className="flex-grow-1">
            <h5 className="mb-0 modal-title">One on One</h5>
            {userName ? <div className="text-muted small">{userName}</div> : null}
          </div>
          <button type="button" className="close" onClick={handleClose} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      ) : (
        <ModalHeader toggle={handleClose} tag="div">
          <div>
            <h5 className="mb-0 modal-title">One on One</h5>
            {userName ? <div className="text-muted small">{userName}</div> : null}
          </div>
        </ModalHeader>
      )}
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', paddingTop: selected ? '1rem' : '0' }}>
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : selected ? (
          <div className="p-1">{renderDetailCard(selected, 0)}</div>
        ) : (
          <>
            {oneOnOnes.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="fas fa-inbox fa-3x mb-3" />
                <p>No one on one records found</p>
              </div>
            ) : (
              <div className="pt-2">
                {oneOnOnes.map((oneOnOne, index) => (
                  <SummaryModalRecordRow
                    key={oneOnOne.id || index}
                    dateText={formatDate(oneOnOne.date_of_incident ?? oneOnOne.dateOfIncident)}
                    description={getOneOnOneListDescription(oneOnOne) || '—'}
                    onClick={() => setSelected(oneOnOne)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default OneOnOneSummaryModal;
